import React, { useState, useMemo, useEffect, useRef } from 'react';
import * as fabric from 'fabric';
import { RequestReportMappingLayoutComponentModel } from '../../../models/BaseRequestModel.tsx';
import { renderMappingLayout } from '../../../services/MappingLayoutService.tsx';
import { REPORT_MAPPING_LAYOUT_URL } from  '../../../const/APIUrl.js';
import { fetchAPI } from '../../../api/GenericFetch.js';
import { fetchWarehouseBeingUsedList, fetchLayoutByWarehouseList } from '../../../services/GetList.js'
import { MappingLayoutCanvas } from '../Layout/MappingLayoutComponents.js';

import {
  Button,
  InputLabel,
} from '@mui/material';

import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

const intervalAutoRefresh = 60 * 1000;

const ReportMappingLayoutComponent: React.FC<RequestReportMappingLayoutComponentModel> = ({locationList, rackTypeList, pageFunction}) => {
  const [ mapLayoutData, setMapLayoutData ] = useState(null);
  const [ isFetched, setIsFetched ] = useState(false);
  const [ selectedLayoutId, setSelectedLayoutId ] = useState("");
  const [ selectedRack, setSelectedRack ] = useState({});
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      if (selectedLayoutId !== "")
      {
        setIsFetched(false);
        loadLayoutData();
      }      
    }, intervalAutoRefresh);
    
    return () => {
      if (intervalRef.current) {        
        clearInterval(intervalRef.current);
      }
    };
  }, [selectedLayoutId]);


  const rackTypeDict = useMemo(() => {
      const rackTypeDict =  [] as any;
      rackTypeList.map(d => {
          rackTypeDict[d.value] = d.color;
      });
      return rackTypeDict;
  }, []);

  const loadLayoutData = async()=> {    
    const responseData = await fetchAPI({url : REPORT_MAPPING_LAYOUT_URL + '?Id=' + selectedLayoutId + '', body: null});  
    setMapLayoutData(responseData);
    if (responseData !== undefined && responseData.LayoutData !== null) setIsFetched(true);
  }

  const handleSubmit = (layoutId)=>{    
    if (layoutId === selectedLayoutId) return;
    setSelectedLayoutId(layoutId);
    setIsFetched(false);
    setSelectedRack({});
  };

  useEffect(() => {
    if (selectedLayoutId !== "") loadLayoutData();
  }, [selectedLayoutId]);

  useEffect(() => {
     // Clean up the interval when current canvas not null
    if (fabricCanvasRef.current) {
      fabricCanvasRef.current.dispose();
    }

    if (isFetched && mapLayoutData !== null)
    {
      fabricCanvasRef.current = new fabric.Canvas(canvasRef.current, {
        width: mapLayoutData.LayoutData.CanvasWidth,
        height: mapLayoutData.LayoutData.CanvasHeight,
      });

      renderMappingLayout(fabricCanvasRef.current, mapLayoutData.ObjectData, rackTypeDict);      
      fabricCanvasRef.current.requestRenderAll();

      if (fabricCanvasRef.current)
      {
          fabricCanvasRef.current.on('mouse:down', (e) => {
              const selectedRack = e.target;            
              if (selectedRack === undefined || selectedRack.isAdditional || selectedRack.isDisabled) return;
              setSelectedRack(selectedRack);
          });
      }
    }

    return () => {
        if (fabricCanvasRef.current) {                
            fabricCanvasRef.current.dispose();
        }
    };
  },[isFetched]);

  useEffect(()=>{
    const handleWindowKeyDown = (event) => {
      if (event.key === 'Escape')
      {
        setSelectedRack({});
      }
    };

    window.addEventListener('keydown', handleWindowKeyDown);
  }, []);

  return(
      <>        
        <div className="row">
          <div className="col-lg-7">
            <FormFilter locationList={locationList} handleSubmit={handleSubmit} />
          </div>
          <div className="col-lg-5">
            <LegendLayout rackTypeList={rackTypeList}/>
          </div>
        </div>        
        <div id="canvas-container" className="row bg-transparent">
          {Object.keys(selectedRack).length > 0 && <ModalRackDetail selectedRack={selectedRack} setSelectedRack={setSelectedRack}/>}
          <div className="canvas-wrapper">
            <MappingLayoutCanvas canvasRef={canvasRef} layoutId={selectedLayoutId} />
          </div>
        </div>
      </>
  );
};

const ModalRackDetail=({selectedRack, setSelectedRack})=>{
  if (Object.keys(selectedRack).length === 0 || selectedRack.RackId === null || selectedRack.RackId == '') return;  
  let container = document.getElementById('canvas-container');
  let containerRect = container !== null ? container.getBoundingClientRect() : {top:0, left:0};
  let top = (containerRect.top + (selectedRack.actualTop !== undefined ? selectedRack.actualTop : selectedRack.top)) - 40;
  let left = (containerRect.left + (selectedRack.actualLeft !== undefined ? selectedRack.actualLeft : selectedRack.left)) + 10;
  
  return(
      <>
        <div className="rack-detail-tooltip position-absolute rounded bg-white p-2 fw-bold" style={{top : top, left: left}}>
          <table className="w-100 table-tooltip">
            <thead>
                <tr>
                  <td colSpan={3} className="text-end tooltip-close text-danger" onClick={()=> { setSelectedRack({}); }}>
                    Esc(X)
                  </td>
                </tr>
            </thead>
            <tbody>
                <tr>
                  <td>Rack Id</td>
                  <td>:</td>
                  <td>{selectedRack.RackId}</td>
                </tr>
                <tr>
                  <td>Rack Name</td>
                  <td>:</td>
                  <td>{selectedRack.RackName}</td>
                </tr>
                <tr>
                  <td>Rack Category</td>
                  <td>:</td>
                  <td>{selectedRack.RackCategoryListName.join(', ')}</td>
                </tr>
                <tr>
                  <td>Notes</td>
                  <td>:</td>
                  <td>{selectedRack.Notes}</td>
                </tr>
              </tbody>
            </table>
        </div>
      </>
  );
}

const FormFilter=({locationList, handleSubmit})=>{
  const [ warehouseList , setWarehouseList ] = useState([]);
  const [ warehouseId, setWarehouseId ] = useState("");
  const [ locationId, setLocationId ] = useState(locationList[0].value);
  const [ layoutList, setLayoutList ] = useState([]);
  const [ layoutId, setLayoutId ] = useState("");

  const initWarehouseList = async() => {
    let warehouseList = await fetchWarehouseBeingUsedList(locationId);
     setWarehouseList(warehouseList);    
  }

  useEffect(() => {
    initWarehouseList();
  }, []);


  const handleLocationChange = async (event) => {    
    let value = event.target.value;
    setLocationId(value);
    setLayoutList([]);
    setWarehouseId("");
    setLayoutId("");
    let warehouseList = await fetchWarehouseBeingUsedList(value);    
    setWarehouseList(warehouseList);        
  }

  const handleWarehouseChange = async (event) => {    
    let currentWarehouseId = event.target.value;
    setWarehouseId(currentWarehouseId);
    let layoutList = await fetchLayoutByWarehouseList(locationId, currentWarehouseId);
    setLayoutList(layoutList);
  }

  return(
    <div className="row mb-3">
      <div className="col-lg-3">
        <InputLabel>Location</InputLabel>
        <Select                
            onChange={handleLocationChange}                
            className="w-100 mui-select-filter"
            name="locationId"                            
            value={locationId}
            >
            {locationList && 
                locationList.map((d) => (
                <MenuItem key={d.value} value={d.value}>{d.text}</MenuItem>
            ))}                         
        </Select>
      </div>
      
      <div className="col-lg-4">
        <InputLabel>Warehouse</InputLabel>
        {warehouseList &&
          <Select
              onChange={handleWarehouseChange}
              className="w-100 mui-select-filter"
              name="warehouseId"                            
              value={warehouseId}
              >
              {warehouseList && 
                  warehouseList.map((d) => (
                  <MenuItem key={d.value} value={d.value}>{d.text}</MenuItem>
              ))}
          </Select>
        }
      </div>

      <div className="col-lg-4">
        <InputLabel>Layout</InputLabel>
        {layoutList &&
          <Select
              onChange={(e)=> setLayoutId(e.target.value) }
              className="w-100 mui-select-filter"
              name="layoutId"
              value={layoutId}
              >
              {layoutList && 
                  layoutList.map((d) => (
                  <MenuItem key={d.value} value={d.value}>{d.text}</MenuItem>
              ))}                         
          </Select>
        }
      </div>
      <div className="col-lg-1 pt-4">
          <InputLabel></InputLabel>
          <Button variant="contained"                 
              onClick={() => {handleSubmit(layoutId);}}>
              Submit
          </Button>
      </div>
    </div>
  );
}

const LegendLayout=({rackTypeList})=>{
  return (
    <>
      {rackTypeList &&
        rackTypeList.map((d) => (
          <div className="legend-box pe-1" key={d.value}>
            <div className="d-inline-block me-1 legend-box-color" style={{backgroundColor: d.color }}></div>
            <div className="d-inline">{d.text}</div>
          </div>
        ))
      }
    </>
  );
}

export default ReportMappingLayoutComponent;